<script>
import AppIcon from '@site/components/common/AppIcon.vue'
import { useConsult } from '@site/composables/useConsult'
import { products } from '@site/data/products'

export default {
  name: 'AppHeader',
  components: { AppIcon },
  data() {
    const { open } = useConsult()
    return {
      open,
      products,
      scrolled: false,
      menuOpen: false,
      productDropdown: false,
      SCROLL_THRESHOLD: 8,
    }
  },
  methods: {
    handleScroll() {
      this.scrolled = window.scrollY > this.SCROLL_THRESHOLD
    },
    closeMenus() {
      this.menuOpen = false
      this.productDropdown = false
    },
  },
  mounted() {
    window.addEventListener('scroll', this.handleScroll, { passive: true })
    this.handleScroll()
    this._routeWatcher = this.$watch(
      () => this.$route.fullPath,
      () => this.closeMenus()
    )
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this.handleScroll)
    if (this._routeWatcher) this._routeWatcher()
  },
}
</script>

<template>
  <header class="app-header" :class="{ 'app-header--scrolled': scrolled }">
    <div class="app-header__mesh" aria-hidden="true">
      <span class="app-header__mesh-blob app-header__mesh-blob--1"></span>
      <span class="app-header__mesh-blob app-header__mesh-blob--2"></span>
    </div>
    <div class="container app-header__inner">
      <router-link to="/" class="app-header__logo">
        <span class="app-header__logo-mark">航</span>
        <span class="app-header__logo-text">航富通</span>
      </router-link>

      <nav class="app-header__nav hide-mobile" aria-label="主导航">
        <div class="app-header__item" @mouseenter="productDropdown = true" @mouseleave="productDropdown = false">
          <span class="app-header__link">产品 <AppIcon name="chevron" :size="12" /></span>
          <div v-if="productDropdown" class="app-header__dropdown">
            <router-link
              v-for="p in products"
              :key="p.type"
              :to="p.link"
              class="app-header__dropdown-item"
            >
              <span class="app-header__dropdown-icon"><AppIcon :name="p.icon" :size="20" /></span>
              <span class="app-header__dropdown-text">
                <strong>{{ p.name }} <span v-if="p.isNew" class="app-header__new">新品</span></strong>
                <small>{{ p.oneLiner }}</small>
              </span>
            </router-link>
          </div>
        </div>
        <router-link to="/compare" class="app-header__link">套餐</router-link>
        <router-link to="/about" class="app-header__link">关于我们</router-link>
      </nav>

      <div class="app-header__actions hide-mobile">
        <button class="btn btn--primary btn--sm" @click="open">立即咨询</button>
      </div>

      <button class="app-header__burger show-mobile" @click="menuOpen = !menuOpen" aria-label="菜单">
        <AppIcon :name="menuOpen ? 'close' : 'menu'" :size="24" />
      </button>
    </div>

    <!-- 移动端菜单 -->
    <transition name="fade">
      <div v-if="menuOpen" class="app-header__mobile">
        <router-link to="/" class="app-header__mobile-link">首页</router-link>
        <router-link to="/product/standard" class="app-header__mobile-link">航富通（标准版）</router-link>
        <router-link to="/product/nova" class="app-header__mobile-link">航富通 Nova（旗舰版）</router-link>
        <router-link to="/compare" class="app-header__mobile-link">套餐对比</router-link>
        <router-link to="/about" class="app-header__mobile-link">关于我们</router-link>
        <button class="btn btn--primary app-header__mobile-cta" @click="open">立即咨询</button>
      </div>
    </transition>
  </header>
</template>


<style lang="scss" scoped>
.app-header {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  // 默认深色半透明背景，保证菜单在任意页面位置都清晰可读
  background: linear-gradient(180deg, rgba(11, 18, 32, 0.72) 0%, rgba(11, 18, 32, 0.4) 60%, transparent 100%);
  transition: background $motion-base $ease-out, box-shadow $motion-base $ease-out, padding $motion-base $ease-out;

  &__mesh {
    position: absolute;
    inset: 0;
    overflow: hidden;
    pointer-events: none;
    opacity: 0;
    transition: opacity $motion-base $ease-out;
  }

  // 滚动后显示背景光斑，为毛玻璃（下拉菜单/移动菜单）提供可糊的内容
  &--scrolled &__mesh { opacity: 1; }

  &__mesh-blob {
    position: absolute;
    border-radius: 50%;
    filter: blur(40px);
    &--1 { width: 280px; height: 280px; top: -80px; right: 12%; background: radial-gradient(circle, rgba(168, 85, 247, 0.5), transparent 65%); }
    &--2 { width: 240px; height: 240px; bottom: -90px; left: 8%; background: radial-gradient(circle, rgba(236, 72, 153, 0.42), transparent 62%); }
  }

  &__inner {
    position: relative;
    z-index: 1;
    display: flex;
    align-items: center;
    justify-content: flex-start;
    height: 72px;
    gap: 24px;
  }

  &__logo {
    display: flex;
    align-items: center;
    gap: 10px;
    color: $color-white;
    font-weight: 900;
    font-size: 20px;

    &-mark {
      width: 36px;
      height: 36px;
      border-radius: 10px;
      background: $gradient-brand;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 18px;
    }

    &-text { letter-spacing: 0.02em; }
  }

  &__nav {
    display: flex;
    align-items: center;
    gap: 6px;
  }

  &__item {
    position: relative;

    // 用伪元素桥接 "产品" 与下拉之间的 hover 间隙，避免 mouseleave 误触发
    &::after {
      content: '';
      position: absolute;
      top: 100%;
      left: 0;
      right: 0;
      height: 12px;
    }
  }

  &__link {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 8px 14px;
    color: rgba(255, 255, 255, 0.82);
    font-size: 15px;
    font-weight: $weight-medium;
    border-radius: $radius-full;
    transition: color $motion-fast, background $motion-fast;
    cursor: pointer;

    svg { opacity: 0.7; }

    &:hover {
      color: $color-white;
      background: rgba(255, 255, 255, 0.08);
    }
  }

  &__dropdown {
    position: absolute;
    top: calc(100% + 12px);
    left: 0;
    min-width: 300px;
    // 真毛玻璃：半透明白玻璃 + 模糊 + 内高光
    background: rgba(255, 255, 255, 0.68);
    -webkit-backdrop-filter: blur(22px) saturate(180%);
    backdrop-filter: blur(22px) saturate(180%);
    border: 1px solid rgba(255, 255, 255, 0.6);
    border-radius: $radius-md;
    box-shadow: 0 8px 28px rgba(11, 18, 32, 0.16), inset 0 1px 0 rgba(255, 255, 255, 0.7);
    padding: 10px;
    display: flex;
    flex-direction: column;
    gap: 2px;
    animation: dropIn 0.18s $ease-out;
  }

  &__dropdown-item {
    display: flex;
    gap: 12px;
    align-items: flex-start;
    padding: 12px;
    border-radius: 10px;
    transition: background 0.18s;
    color: $color-text;

    &:hover { background: $color-bg-light; }

    strong { display: block; font-size: 15px; font-weight: $weight-semibold; color: $color-text; display: flex; align-items: center; gap: 6px; }
    small { color: $color-text-muted; font-size: 12px; line-height: 1.4; margin-top: 2px; }
  }

  &__new {
    display: inline-flex;
    align-items: center;
    font-size: 10px;
    font-weight: $weight-bold;
    color: $color-white;
    background: linear-gradient(120deg, $color-dopamine-orange, $color-dopamine-pink);
    border-radius: $radius-full;
    padding: 2px 8px;
    letter-spacing: 0.02em;
  }

  &__dropdown-icon {
    width: 38px;
    height: 38px;
    border-radius: 10px;
    background: $color-primary-soft;
    color: $color-primary;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;

    svg { width: 20px; height: 20px; }
  }

  &__actions {
    display: flex;
    align-items: center;
    gap: 16px;
    margin-left: auto;
  }

  &__burger {
    color: $color-white;
    display: inline-flex;
    padding: 6px;
  }

  // 桌面端：默认展示导航与操作，隐藏汉堡（避免全局工具类被 scoped 优先级覆盖）
  @media (min-width: calc($bp-sm + 1px)) {
    &__burger { display: none; }
  }

  // 移动端：隐藏导航与操作按钮，展示汉堡，防止窄屏下 flex 换行错位
  @media (max-width: $bp-sm) {
    &__nav, &__actions { display: none; }
    &__burger { display: inline-flex; }
  }

  &__mobile {
    position: absolute;
    top: 72px;
    left: 0;
    right: 0;
    background: rgba(11, 18, 32, 0.96);
    backdrop-filter: blur(12px);
    padding: 16px 24px 24px;
    display: flex;
    flex-direction: column;
    gap: 8px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.08);

    &-link {
      color: $color-white;
      padding: 12px 8px;
      font-size: 17px;
      border-bottom: 1px solid rgba(255, 255, 255, 0.06);
      &:hover { color: $color-secondary-light; }
    }

    &-cta {
      margin-top: 12px;
      justify-content: center;
    }
  }

  // 滚动后收紧（毛玻璃）
  &--scrolled {
    background: rgba(11, 18, 32, 0.7);
    backdrop-filter: $glass-blur;
    box-shadow: 0 1px 0 rgba(255, 255, 255, 0.06), 0 4px 20px rgba(0, 0, 0, 0.14);

    &__inner { height: 64px; }
  }
}

@keyframes dropIn {
  from { opacity: 0; transform: translateY(-6px); }
  to { opacity: 1; transform: translateY(0); }
}

.fade-enter-active, .fade-leave-active { transition: opacity 0.25s; }
.fade-enter-from, .fade-leave-to { opacity: 0; }
</style>



// WEBPACK FOOTER //
// src/site/components/layout/AppHeader.vue